import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Clock, TrendingUp, Users, Award, MessageSquare, ArrowUpRight, Gift } from 'lucide-react';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import EarnCreditsCard from '../components/EarnCreditsCard';
import SupportChronosCard from '../components/SupportChronosCard';
import GamificationCard from '../components/GamificationCard';
import TopCreatorsSection from '../components/TopCreatorsSection';
import ActivityFeed from '../components/ActivityFeed';
import FeaturedCoursesCard from '../components/FeaturedCoursesCard';

export default function Dashboard() {
  const { user, socket } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    balance: 0,
    pending: 0,
    completed: 0,
    rating: 0
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    
    // Listen for real-time notifications
    if (socket) {
      socket.on('notification', (notification) => {
        setNotifications(prev => [notification, ...prev]);
        toast.info(notification.title, {
          description: notification.content
        });
      });
    }
    
    return () => {
      if (socket) {
        socket.off('notification');
      }
    };
  }, [socket]);

  const fetchDashboardData = async () => {
    try {
      const [transactionsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/transactions?limit=5`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true })
      ]);

      setRecentTransactions(transactionsRes.data);
      setNotifications(notificationsRes.data);

      // Calculate stats
      const pending = transactionsRes.data.filter(t => t.status === 'pending').length;
      const completed = transactionsRes.data.filter(t => t.status === 'completed').length;

      setStats({
        balance: user?.credits_balance || 0,
        pending,
        completed,
        rating: user?.reputation?.rating_avg || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const getTrustLevelColor = (level) => {
    const colors = {
      'newcomer': 'bg-gray-100 text-gray-600',
      'trusted': 'bg-blue-100 text-blue-600',
      'expert': 'bg-purple-100 text-purple-600'
    };
    return colors[level] || colors.newcomer;
  };

  return (
    <Layout>
      <div data-testid="dashboard" className="space-y-8">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl p-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{t('dashboard.welcome')}, {user?.name}!</h1>
              <p className="text-teal-100 text-lg">{t('dashboard.subtitle')}</p>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold mb-1">{stats.balance}</div>
              <div className="text-teal-100">{t('dashboard.credits')}</div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 border-l-4 border-teal-500 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('dashboard.pending')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pending}</p>
              </div>
              <Clock className="h-10 w-10 text-teal-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-cyan-500 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('dashboard.completed')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.completed}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-cyan-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-yellow-500 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('dashboard.rating')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.rating.toFixed(1)}</p>
              </div>
              <Award className="h-10 w-10 text-yellow-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-purple-500 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('dashboard.trustLevel')}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mt-2 ${getTrustLevelColor(user?.reputation?.trust_level)}`}>
                  {user?.reputation?.trust_level || 'newcomer'}
                </span>
              </div>
              <Users className="h-10 w-10 text-purple-500" />
            </div>
          </Card>
        </div>

        {/* Earn Credits Card */}
        <EarnCreditsCard />

        {/* Gamification Card */}
        <GamificationCard />

        {/* Top Creators Section */}
        <TopCreatorsSection />

        {/* Featured Courses Card */}
        <FeaturedCoursesCard />

        {/* Support Chronos Card */}
        <SupportChronosCard />

        {/* Referral Quick Link */}
        <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6 text-purple-500" />
              <div>
                <h4 className="font-bold text-gray-900">{t('referral.inviteEarn')}</h4>
                <p className="text-sm text-gray-600">{t('referral.inviteEarnDesc')}</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/referrals')}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              {t('referral.viewDetails')}
            </Button>
          </div>
        </Card>

        {/* Activity Feed & Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ActivityFeed />
          {/* Recent Transactions */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{t('dashboard.recentActivity')}</h2>
              <Button
                data-testid="view-all-transactions-button"
                variant="ghost"
                onClick={() => navigate('/transactions')}
                className="text-teal-500 hover:text-teal-600"
              >
                {t('dashboard.viewAll')} <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              {recentTransactions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('dashboard.noRecentTransactions')}</p>
              ) : (
                recentTransactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-teal-500">{transaction.hours}h</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        transaction.status === 'completed' ? 'bg-green-100 text-green-600' :
                        transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Notifications */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{t('dashboard.notifications')}</h2>
              <MessageSquare className="h-5 w-5 text-gray-400" />
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('dashboard.noNotifications')}</p>
              ) : (
                notifications.slice(0, 5).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      notification.read ? 'bg-gray-50 border-gray-300' : 'bg-teal-50 border-teal-500'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{notification.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{notification.content}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              data-testid="browse-users-button"
              onClick={() => navigate('/browse')}
              className="bg-teal-500 hover:bg-teal-600 text-white p-6 h-auto flex flex-col items-center gap-2 rounded-xl"
            >
              <Users className="h-8 w-8" />
              <span>{t('dashboard.browseUsers')}</span>
            </Button>
            <Button
              data-testid="view-transactions-button"
              onClick={() => navigate('/transactions')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white p-6 h-auto flex flex-col items-center gap-2 rounded-xl"
            >
              <Clock className="h-8 w-8" />
              <span>{t('dashboard.transactions')}</span>
            </Button>
            <Button
              data-testid="open-messages-button"
              onClick={() => navigate('/messages')}
              className="bg-purple-500 hover:bg-purple-600 text-white p-6 h-auto flex flex-col items-center gap-2 rounded-xl"
            >
              <MessageSquare className="h-8 w-8" />
              <span>{t('dashboard.messages')}</span>
            </Button>
            <Button
              data-testid="view-profile-button"
              onClick={() => navigate('/profile')}
              className="bg-yellow-500 hover:bg-yellow-600 text-white p-6 h-auto flex flex-col items-center gap-2 rounded-xl"
            >
              <Award className="h-8 w-8" />
              <span>{t('dashboard.profile')}</span>
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}